import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Reveal from "@/components/site/Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function NewLaunch() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const yProduct = useTransform(scrollYProgress, [0, 1], ["8%", "-12%"]);
    const rotate = useTransform(scrollYProgress, [0, 1], [-4, 4]);
    const yMonogram = useTransform(scrollYProgress, [0, 1], ["18%", "-18%"]);

    const month = new Date().toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
    });

    return (
        <section
            ref={ref}
            data-testid="new-launch-section"
            className="relative overflow-hidden border-t border-white/10 bg-gradient-to-b from-[#0e0e0e] via-[#1a1614] to-[#0a0a0a]"
        >
            {/* Soft warm glow */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                    background:
                        "radial-gradient(60% 50% at 70% 45%, rgba(213,170,120,0.18) 0%, rgba(0,0,0,0) 70%)",
                }}
            />
            {/* Concentric arcs */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 70% 50%, rgba(255,255,255,0.6) 0 1px, transparent 1px 240px), radial-gradient(circle at 70% 50%, rgba(255,255,255,0.6) 0 1px, transparent 1px 480px), radial-gradient(circle at 70% 50%, rgba(255,255,255,0.6) 0 1px, transparent 1px 720px)",
                }}
            />

            {/* Giant ghost monogram (parallax) */}
            <motion.div
                style={{ y: yMonogram }}
                aria-hidden
                className="pointer-events-none absolute -top-10 right-[-4vw] select-none"
            >
                <span className="font-serif italic text-white/[0.03] text-[42vw] leading-none tracking-tighter">
                    H&amp;H
                </span>
            </motion.div>

            <div className="relative px-6 sm:px-10 lg:px-16 py-28 lg:py-40">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    {/* Text */}
                    <div className="lg:col-span-6 relative z-10">
                        <Reveal>
                            <div className="inline-flex items-center gap-3 px-3 py-1.5 border border-white/20 mb-8">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#d5aa78] animate-pulse" />
                                <span className="text-[10px] uppercase tracking-[0.32em] text-white/70 font-mono-custom">
                                    New · {month}
                                </span>
                            </div>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
                                The Monthly Edition
                            </p>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <h2 className="font-serif text-5xl sm:text-6xl lg:text-[7.4vw] font-medium leading-[0.92] tracking-tight">
                                <span className="block">Rugged</span>
                                <span className="block italic font-normal text-[#d5aa78]">
                                    Vintage
                                </span>
                                <span className="block">Jacket.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.22}>
                            <p className="mt-10 max-w-md text-base lg:text-lg text-white/70 font-light leading-relaxed">
                                Handcrafted in premium leather with a rugged vintage finish, built for those who carry timeless{" "}
                                <em>style effortlessly</em>.
                            </p>
                        </Reveal>
                        <Reveal delay={0.32}>
                            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md border-t border-white/10 pt-8">
                                <Stat label="Edition" value="014" />
                                <Stat label="Hide" value="Leather" />
                                <Stat label="Crafted in" value="Bengaluru" />
                            </div>
                        </Reveal>
                        <Reveal delay={0.42}>
                            <Link
                                to="/#appointment"
                                data-testid="new-launch-cta"
                                className="group mt-12 inline-flex items-center gap-4 border border-white/40 px-9 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#d5aa78] hover:text-black hover:border-[#d5aa78] transition-all duration-500"
                            >
                                Reserve a fitting
                                <ArrowUpRight
                                    size={14}
                                    className="transition-transform duration-500 group-hover:rotate-45"
                                />
                            </Link>
                        </Reveal>
                    </div>

                    {/* Floating product */}
                    <div className="lg:col-span-6 relative h-[60vh] lg:h-[78vh] flex items-center justify-center">
                        <motion.div
                            style={{ y: yProduct, rotate }}
                            className="relative w-full h-full will-change-transform"
                        >
                            {/* Soft floor shadow */}
                            <div
                                aria-hidden
                                className="absolute left-1/2 -translate-x-1/2 bottom-[12%] w-3/4 h-10 rounded-[50%] blur-2xl opacity-60"
                                style={{
                                    background:
                                        "radial-gradient(50% 100% at 50% 50%, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 70%)",
                                }}
                            />
                            <motion.img
                                src="/brand/Jack.png"
                                alt="The Laptop Bag"
                                className="absolute inset-0 m-auto max-h-full max-w-full object-contain drop-shadow-[0_40px_60px_rgba(0,0,0,0.55)]"
                                animate={{ y: [0, -10, 0] }}
                                transition={{
                                    duration: 6,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                draggable="false"
                            />
                        </motion.div>

                        {/* Vertical edge label */}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-3 [writing-mode:vertical-rl] rotate-180 text-[10px] uppercase tracking-[0.4em] text-white/40 font-mono-custom">
                            <span>Edition · 014</span>
                            <span>·</span>
                            <span>Hand-woven</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <p className="font-serif text-2xl lg:text-3xl text-white">{value}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.28em] text-white/40">
                {label}
            </p>
        </div>
    );
}
